import { lazy } from "react";

import Redirect from "@router/redirect";

const AccountPage = lazy(() => import("./pages/account/account-page"));
const ChangeEmailPage = lazy(() => import("./pages/change-email/change-email"));
const ConnectionsPage = lazy(
  () => import("./pages/connections/connections-page"),
);
const GoodbyePage = lazy(() => import("./pages/goodbye/goodbye-page"));
const SigninPage = lazy(() => import("./pages/signin/signin-page"));
const SigninJobPage = lazy(() => import("./pages/signin/job/signin-job-page"));
const SignupPage = lazy(() => import("./pages/signup/signup-page"));
const SignupJobPage = lazy(() => import("./pages/signup/job/signup-job-page"));
const ForgottenPasswordPage = lazy(
  () => import("./pages/forgotten-password/forgotten-password-page"),
);
const ResetPasswordPage = lazy(
  () => import("./pages/reset-password/reset-password-page"),
);
const SecurityPage = lazy(() => import("./pages/security/security-page"));
const SessionsPage = lazy(() => import("./pages/sessions/sessions-page"));
const MenuPage = lazy(() => import("./pages/menu/menu-page"));
const OauthProfilesPage = lazy(
  () => import("./pages/oauth-profiles/oauth-profiles-page"),
);
const OauthConnectPage = lazy(
  () => import("./pages/oauth-connect/oauth-connect-page"),
);
const OauthRedirectPage = lazy(
  () => import("./pages/oauth-redirect/oauth-redirect-page"),
);
const OauthSigninPage = lazy(
  () => import("./pages/oauth-signin/oauth-signin-page"),
);
const OauthSignupPage = lazy(
  () => import("./pages/oauth-signup/oauth-signup-page"),
);
const SamlErrorPage = lazy(() => import("./pages/saml-error/saml-error-page"));
const ReConsentPage = lazy(() => import("./pages/re-consent/re-consent-page"));

const TermsAndConditionsPage = lazy(
  () => import("./pages/tac/terms-and-conditions"),
);

type User = {
  id?: string;
  email?: string;
  language?: string;
  [key: string]: any;
};

type RouteContext = {
  user?: User;
};

type RouteRequest = {
  searchParams: {
    [key: string]: any;
  };
  [key: string]: any;
};

type Route = {
  path: string;
  authRoute?: boolean;
  action: (request: RouteRequest, context: RouteContext) => JSX.Element;
};

const routes: Route[] = [
  {
    path: "/",
    action(request, context) {
      if (!context.user) return <Redirect replace to="/signin" />;
      return <Redirect replace to="/connections" />;
    },
  },
  {
    // From the external service signin redirect
    path: "/authorize",
    action(request) {
      return <SigninPage {...request} />;
    },
  },
  {
    path: "/tac",
    action() {
      return <TermsAndConditionsPage />;
    },
  },
  {
    path: "/goodbye",
    action() {
      return <GoodbyePage />;
    },
  },
  {
    path: "/signup",
    action(request) {
      return <SignupPage {...request} />;
    },
  },
  {
    path: "/signup-job",
    action(request) {
      return <SignupJobPage {...request} />;
    },
  },
  {
    path: "/signin",
    action(request) {
      return <SigninPage {...request} />;
    },
  },
  {
    path: "/signin-job",
    action(request) {
      return <SigninJobPage {...request} />;
    },
  },
  {
    path: "/forgot-password",
    action() {
      return <ForgottenPasswordPage />;
    },
  },
  {
    path: "/reset-password",
    action(request) {
      return <ResetPasswordPage {...request} />;
    },
  },
  {
    path: "/change-email",
    authRoute: true,
    action(request) {
      return <ChangeEmailPage {...request} />;
    },
  },
  {
    path: "/account",
    authRoute: true,
    action() {
      return (
        <MenuPage>
          <AccountPage />
        </MenuPage>
      );
    },
  },
  {
    path: "/security",
    authRoute: true,
    action() {
      return (
        <MenuPage>
          <SecurityPage />
        </MenuPage>
      );
    },
  },
  {
    path: "/connections",
    authRoute: true,
    action() {
      return (
        <MenuPage>
          <ConnectionsPage />
        </MenuPage>
      );
    },
  },
  {
    path: "/oauth-profiles",
    authRoute: true,
    action() {
      return (
        <MenuPage>
          <OauthProfilesPage />
        </MenuPage>
      );
    },
  },
  {
    path: "/sessions",
    authRoute: true,
    action() {
      return (
        <MenuPage>
          <SessionsPage />
        </MenuPage>
      );
    },
  },
  {
    path: "/(github|twitter)/callback",
    action(request, context) {
      if (context.user?.id) {
        return <OauthConnectPage {...request} />;
      } else {
        return <OauthSigninPage {...request} />;
      }
    },
  },
  {
    path: "/(github|twitter)/callback/redirect",
    action(request) {
      return <OauthRedirectPage {...request} />;
    },
  },
  {
    path: "/(github|twitter)/callback/signup",
    action(request) {
      return <OauthSignupPage {...request} />;
    },
  },
  {
    path: "/error/saml",
    action(request) {
      return <SamlErrorPage {...request} />;
    },
  },
  {
    path: "/re-consent",
    action() {
      return <ReConsentPage />;
    },
  },
];

export default routes;
